import { Environment } from "@/core/generated/api/constants/environment"
import { AUTH_KEY, getItem, removeItem } from "../../browserStorage"
import { fetcher } from "../index"
import { get } from "lodash"
import { LAST_SELECTED_SPACE_UID_KEY } from "@/components/authentication"
import { DISMISSED_BANNERS_LOCAL_STORAGE_KEY } from "@/core/components/HeroBanner/helpers/dismissedBanners"
import {
  createMagicLinkSender,
  type MagicLinkRequestArgs,
} from "./magicLinkRequest"
import {
  COOKIE_AUTH_ENABLED,
  getAuthFetchOptions,
} from "@/core/generated/api/auth_cookies"

export const getSpaceFromToken = () => {
  const authToken = getItem(AUTH_KEY)

  return parseJwt(authToken)?.space
}

export function parseJwt(token) {
  if (!token) {
    return
  }
  const base64Url = token.split(".")[1]
  const base64 = base64Url.replace("-", "+").replace("_", "/")
  return JSON.parse(window.atob(base64))
}

// export const magicLink = ({
//   email,
//   returnUrl,
// }: {
//   email: string
//   returnUrl?: string
// }) => {
//   return fetcher("/auth/magic_link", {
//     method: "POST",
//     body: JSON.stringify({ email, returnUrl }),
//   })
// }
export const magicLink = async ({
  email,
  returnUrl,
  spaceId,
}: MagicLinkRequestArgs) => {
  const sendMagicLink = createMagicLinkSender(fetcher)
  return sendMagicLink({ email, returnUrl, spaceId })
}

// export const getAuthLink = (
//   strategy: "google" | "microsoft" | "keycloak",
//   params?: { returnUrl?: string },
// ) => {
//   let url: string = getAuthUrl()
//   url = `${url}/auth/${strategy}?success_uri=/?returnUrl=${params?.returnUrl ?? "/"}`

//   return url
// }
export const getAuthLink = (
  strategy: "google" | "microsoft" | "keycloak",
  params?: { returnUrl?: string | null },
) => {
  const url = new URL(`${getAuthUrl()}/auth/${strategy}`)
  const returnPath = getSafeReturnPath(params?.returnUrl)

  url.searchParams.set(
    "success_uri",
    COOKIE_AUTH_ENABLED
      ? returnPath
      : `/?returnUrl=${encodeURIComponent(returnPath)}`,
  )

  return url.toString()
}

export const establishCookieSession = async (token: string): Promise<void> => {
  if (!COOKIE_AUTH_ENABLED) return

  const apiUrl = process.env["NEXT_PUBLIC_API_URL"]
  if (!apiUrl) {
    throw new Error("Missing NEXT_PUBLIC_API_URL")
  }

  const response = await fetch(`${apiUrl.replace(/\/$/, "")}/auth/session`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token }),
  })

  if (!response.ok) {
    throw new Error(`Failed to establish session: ${response.status}`)
  }
}

const getAuthUrl = (): string => {
  let url: string = `${process.env["NEXT_PUBLIC_API_URL"]}`
  if (process.env.NEXT_PUBLIC_API_NXT_URL) {
    url = `${process.env.NEXT_PUBLIC_API_NXT_URL}/nxt`
  }

  return url
}

export const signUp = async ({
  firstname,
  lastname,
  phone,
  title,
}: {
  firstname: string
  lastname: string
  phone: string
  title: string
}) => {
  return await fetcher(`/auth/signup`, {
    method: "POST",
    body: JSON.stringify({ firstname, lastname, phone, title }),
  })
}

export const signOut = async () => {
  const token = getItem(AUTH_KEY) ?? ""
  const apiNxtUrl = process.env.NEXT_PUBLIC_API_NXT_URL

  await callSignOutEndpoint(apiNxtUrl, "/nxt/auth/signout", token).catch(
    (reason) => {
      console.warn("NXT signout endpoint failed", reason)
    },
  )

  if (window._cio) {
    window._cio.reset()
  }

  const dismissedBanners = localStorage.getItem(
    DISMISSED_BANNERS_LOCAL_STORAGE_KEY,
  )

  sessionStorage.removeItem(LAST_SELECTED_SPACE_UID_KEY)

  localStorage.clear()

  localStorage.setItem(DISMISSED_BANNERS_LOCAL_STORAGE_KEY, dismissedBanners)

  removeItem(AUTH_KEY)
  window.location.assign("/")
}

const callSignOutEndpoint = async (
  baseUrl: string | undefined,
  path: string,
  token: string,
): Promise<void> => {
  if (!baseUrl) {
    throw new Error(`Missing signout API URL for ${path}`)
  }

  const response = await fetch(
    `${baseUrl.replace(/\/$/, "")}${path}`,
    getAuthFetchOptions(token, { method: "POST" }),
  )

  if (!response.ok) {
    throw new Error(`${path} failed with status ${response.status}`)
  }
}

const getSafeReturnPath = (value?: string | null): string => {
  if (!value) return "/"

  try {
    const url = new URL(value, window.location.origin)
    if (url.origin !== window.location.origin) return "/"

    return `${url.pathname}${url.search}${url.hash}` || "/"
  } catch {
    return value.startsWith("/") && !value.startsWith("//") ? value : "/"
  }
}
